import React, { useEffect, useMemo, useState } from 'react'
import { Document, Page, Text, View, Image } from '@react-pdf/renderer'

import PropTypes from 'prop-types'
import styles from './styles'

const ServiceOrderPDF = ({
  budget,
  property,
  serviceOrder,
  agencyLogo,
  dateBudget = null,
  showReferaValue,
  agency,
  systemParameters,
  selectedOptions = {},
  timelineEvents = {},
  notesComments = [],
}) => {
  const allowedExtensions = ['.jpeg', '.png', '.jpg', '.webp', '.bmp']
  const formatterNumberOnly = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  })

  const [coverageMessage, setCoverageMessage] = useState('')

  const shouldShowCover = agency?.budgetCover === true

  const budgetGeneralCover = systemParameters?.find(
    param => param.name === 'budget_general_cover'
  )?.value

  const coverImage = agency?.budgetCoverPicture || budgetGeneralCover || null

  const handleGetMessage = () => {
    const coverageAmount = budget?.totalPrice * (budget?.coverageValue?.coveragePercentage / 100)

    if (coverageAmount > 0) {
      setCoverageMessage(
        'Este serviço possui a Cobertura da Refera! A refera se compromete a resolver qualquer problema relacionado ao serviço executado, desde que esteja dentro do prazo da garantia e dentro do limite de valor.'
      )
    } else if (isNaN(coverageAmount)) {
      setCoverageMessage(
        'Este serviço possui a Cobertura especial da Refera! A refera se compromete a resolver qualquer problema relacionado ao serviço executado, desde que esteja dentro do prazo da garantia.'
      )
    } else if (coverageAmount === 0) {
      setCoverageMessage('Este serviço não possui a cobertura da refera.')
    }
  }

  useEffect(() => {
    handleGetMessage()
  }, [budget])

  // Arquivos de nota fiscal (apenas PDF), coletados dos anexos do chamado
  const serviceOrderAttachments = serviceOrder?.attachments?.toJS
    ? serviceOrder.attachments.toJS()
    : serviceOrder?.attachments || []

  const orderPicturesOpen = useMemo(() => {
    try {
      return (serviceOrderAttachments || [])
        .filter(att => {
          const fileType = att?.fileType || att?.get?.('fileType')
          const fileUrl = att?.file || att?.get?.('file')
          const isOpenPicture = fileType === 'pictures_order'
          const isImageExt =
            typeof fileUrl === 'string' &&
            allowedExtensions.some(ext => fileUrl?.toLowerCase()?.includes(ext))
          return isOpenPicture && isImageExt
        })
        .map(att => ({ id: att?.id || att?.get?.('id'), picture: att?.file || att?.get?.('file') }))
    } catch (e) {
      return []
    }
  }, [serviceOrderAttachments])

  const orderPicturesFinished = useMemo(() => {
    try {
      return (serviceOrderAttachments || [])
        .filter(att => {
          const fileType = att?.fileType || att?.get?.('fileType')
          const fileUrl = att?.file || att?.get?.('file')
          const isFinishedPicture = fileType === 'execution_proof'
          const isImageExt =
            typeof fileUrl === 'string' &&
            allowedExtensions.some(ext => fileUrl?.toLowerCase()?.includes(ext))
          return isFinishedPicture && isImageExt
        })
        .map(att => ({ id: att?.id || att?.get?.('id'), picture: att?.file || att?.get?.('file') }))
    } catch (e) {
      return []
    }
  }, [serviceOrderAttachments])

  const handlePercentageToValue = (percentage, totalReference) => {
    return (percentage * totalReference) / 100.0
  }

  const referaPlusAgencyPercentage = useMemo(() => {
    const agencyPercentage = (budget?.agencyCompletionPercentage || 0) / 100
    const referaPercentage = (budget?.referaCompletionPercentage || 0) / 100

    const totalPercentage = agencyPercentage + referaPercentage

    return Math.round(totalPercentage * 100)
  }, [budget])

  const tradesmanPercentage = useMemo(() => {
    const totalPercentage = referaPlusAgencyPercentage
    return 100 - totalPercentage
  }, [referaPlusAgencyPercentage])

  const itemValueCalculate = (itemValue, index = undefined) => {
    const percentageToAdd =
      ((budget?.priceRefera + budget?.priceAgency) * 100) / budget?.totalPriceServiceProvider

    let itemPrice = handlePercentageToValue(percentageToAdd, itemValue) + itemValue

    itemPrice = Math.round(itemPrice * 100) / 100

    return index !== undefined ? formatterNumberOnly.format(itemPrice) : itemPrice
  }

  const handleTotalItems = useMemo(() => {
    const items = budget?.budgetItems ?? []
    return items.reduce((sum, { price }) => sum + itemValueCalculate(price), 0)
  }, [budget?.budgetItems])

  const isNeutralContractType =
    serviceOrder?.agency?.get('contractType') !== 'i_got_rid' &&
    serviceOrder?.agency?.get('contractType') !== 'i_answer'

  const formatDateTime = date => {
    try {
      const d = new Date(date || new Date())
      return new Intl.DateTimeFormat('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(d)
    } catch (e) {
      return ''
    }
  }

  const isOpt = name => Boolean(selectedOptions?.[name])

  return (
    <Document>
      {shouldShowCover && coverImage && (
        <Page size="A4" style={styles.page}>
          <View style={styles.section}>
            <Image style={styles.attachmentImage} src={coverImage} />
          </View>
        </Page>
      )}

      <Page size="A4" style={styles.page}>
        <View style={isNeutralContractType ? styles.neutralBackground : styles.header} fixed>
          <View>
            {isNeutralContractType &&
            agencyLogo &&
            serviceOrder?.agency?.get('contractType') !== 'SAAS' ? (
              <Image style={styles.headerLogo} src={agencyLogo} />
            ) : null}
          </View>
        </View>
        {agencyLogo && <Image style={styles.agencyLogo} src={agencyLogo} />}
        <View style={styles.pageSpace}>
          <Text style={isNeutralContractType ? styles.grayText : styles.blackText}>
            Chamado <span style={styles.gray32}>•</span> {serviceOrder?.agency?.get('name')}
          </Text>
          <View>
            <Text style={styles.headerText}>
              Chamado #{serviceOrder?.get?.('id') || serviceOrder?.id}
            </Text>
          </View>
          <View style={isNeutralContractType ? styles.grayBackground : styles.backgroundText}>
            <Text style={isNeutralContractType ? styles.blackText : styles.blueText}>
              Dados básicos
            </Text>
          </View>
          <View style={styles.budgetContainer}>
            {isOpt('openDate') && (
              <View style={styles.descriptionText}>
                <Text style={styles.title}>Data de abertura do chamado</Text>
                <Text style={styles.description}>
                  {formatDateTime(serviceOrder?.createdAt || dateBudget)}
                </Text>
              </View>
            )}
            {isOpt('requesterName') && (
              <View style={styles.descriptionText}>
                <Text style={styles.title}>Nome do solicitante</Text>
                <Text style={styles.description}>
                  {serviceOrder?.contactName ||
                    serviceOrder?.get?.('contactName') ||
                    'Não informado'}
                </Text>
              </View>
            )}
            {isOpt('ownerName') && (
              <View style={styles.descriptionText}>
                <Text style={styles.title}>Nome do proprietário</Text>
                <Text style={styles.description}>
                  {serviceOrder?.propertyName ||
                    serviceOrder?.get?.('propertyName') ||
                    'Não informado'}
                </Text>
              </View>
            )}
            {isOpt('providerNameCnpj') && budget?.tradesman?.companyName && (
              <View style={styles.descriptionText}>
                <Text style={styles.title}>Prestador</Text>
                <Text style={styles.description}>
                  {`${budget.tradesman.companyName} - ${budget.tradesman.companyCnpj || ''}`}
                </Text>
              </View>
            )}
            {isOpt('address') && (
              <View style={styles.descriptionText}>
                <Text style={styles.title}>Endereço do imóvel</Text>
                <Text style={styles.description}>{property}</Text>
              </View>
            )}
            {isOpt('propertyCode') && (
              <View style={styles.descriptionText}>
                <Text style={styles.title}>Código do imóvel</Text>
                <Text style={styles.description}>
                  {serviceOrder?.propertyCode || serviceOrder?.get?.('propertyCode')}
                </Text>
              </View>
            )}
            {((isOpt('timelineOpen') && timelineEvents?.open) ||
              (isOpt('timelineBudgetReceived') && timelineEvents?.budgetReceived) ||
              (isOpt('timelineBudgetApproved') && timelineEvents?.budgetApproved) ||
              (isOpt('timelineFinished') && timelineEvents?.finished) ||
              serviceOrder?.datetimeFinished ||
              serviceOrder?.get?.('datetimeFinished') ||
              serviceOrder?.dateAndTimeFinish ||
              serviceOrder?.get?.('dateAndTimeFinish')) && (
              <>
                <View style={isNeutralContractType ? styles.grayBackground : styles.backgroundText}>
                  <Text style={isNeutralContractType ? styles.blackText : styles.blueText}>
                    Mais detalhes
                  </Text>
                </View>
                <View style={styles.budgetContainer}>
                  {isOpt('timelineOpen') && timelineEvents?.open && (
                    <View style={styles.descriptionText}>
                      <Text style={styles.title}>{timelineEvents.open.eventName}</Text>
                      <Text style={styles.description}>
                        {timelineEvents.open.userName} - {formatDateTime(timelineEvents.open.date)}
                      </Text>
                    </View>
                  )}
                  {isOpt('timelineBudgetReceived') && timelineEvents?.budgetReceived && (
                    <View style={styles.descriptionText}>
                      <Text style={styles.title}>{timelineEvents.budgetReceived.eventName}</Text>
                      <Text style={styles.description}>
                        {timelineEvents.budgetReceived.userName} -{' '}
                        {formatDateTime(timelineEvents.budgetReceived.date)}
                      </Text>
                    </View>
                  )}
                  {isOpt('timelineBudgetApproved') && timelineEvents?.budgetApproved && (
                    <View style={styles.descriptionText}>
                      <Text style={styles.title}>{timelineEvents.budgetApproved.eventName}</Text>
                      <Text style={styles.description}>
                        {timelineEvents.budgetApproved.userName} -{' '}
                        {formatDateTime(timelineEvents.budgetApproved.date)}
                      </Text>
                    </View>
                  )}
                  {isOpt('timelineFinished') && timelineEvents?.finished && (
                    <View style={styles.descriptionText}>
                      <Text style={styles.title}>{timelineEvents.finished.eventName}</Text>
                      <Text style={styles.description}>
                        {timelineEvents.finished.userName} -{' '}
                        {formatDateTime(timelineEvents.finished.date)}
                      </Text>
                    </View>
                  )}
                  {(serviceOrder?.datetimeFinished ||
                    serviceOrder?.get?.('datetimeFinished') ||
                    serviceOrder?.dateAndTimeFinish ||
                    serviceOrder?.get?.('dateAndTimeFinish')) && (
                    <View style={styles.descriptionText}>
                      <Text style={styles.title}>Data do chamado finalizado</Text>
                      <Text style={styles.description}>
                        {formatDateTime(
                          serviceOrder?.datetimeFinished ||
                            serviceOrder?.get?.('datetimeFinished') ||
                            serviceOrder?.dateAndTimeFinish ||
                            serviceOrder?.get?.('dateAndTimeFinish')
                        )}
                      </Text>
                    </View>
                  )}
                </View>
              </>
            )}
          </View>
        </View>
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
          fixed
        />
      </Page>

      {isOpt('commentsPublic') && notesComments?.length > 0 && (
        <Page size="A4" style={styles.page}>
          <View style={isNeutralContractType ? styles.neutralBackground : styles.header} fixed>
            <View>
              {isNeutralContractType &&
              agencyLogo &&
              serviceOrder?.agency?.get('contractType') !== 'SAAS' ? (
                <Image style={styles.headerLogo} src={agencyLogo} />
              ) : null}
            </View>
          </View>
          {agencyLogo && <Image style={styles.agencyLogo} src={agencyLogo} />}
          <View style={styles.pageSpace}>
            <View style={isNeutralContractType ? styles.grayBackground : styles.backgroundText}>
              <Text style={isNeutralContractType ? styles.blackText : styles.blueText}>
                Comentários
              </Text>
            </View>
            <View style={styles.budgetContainer}>
              {notesComments.map(note => (
                <View key={note.id} style={styles.commentContainer}>
                  <View style={styles.commentHeader}>
                    <Text style={styles.commentUser}>
                      {note.userName} -{' '}
                      <Text style={styles.commentDate}>{formatDateTime(note.date)}</Text>
                    </Text>
                  </View>
                  <View style={styles.commentContent}>
                    <Text style={styles.commentText}>{note.content}</Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
          <Text
            style={styles.pageNumber}
            render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
            fixed
          />
        </Page>
      )}

      <Page size="A4" style={styles.page}>
        <View style={isNeutralContractType ? styles.neutralBackground : styles.header} fixed>
          <View>
            {isNeutralContractType &&
            agencyLogo &&
            serviceOrder?.agency?.get('contractType') !== 'SAAS' ? (
              <Image style={styles.headerLogo} src={agencyLogo} />
            ) : null}
          </View>
        </View>
        {agencyLogo && <Image style={styles.agencyLogo} src={agencyLogo} />}
        <View style={styles.pageSpace}>
          <View style={isNeutralContractType ? styles.grayBackground : styles.backgroundText}>
            <Text style={isNeutralContractType ? styles.blackText : styles.blueText}>
              Dados do serviço
            </Text>
          </View>
          <View style={styles.budgetContainer}>
            {isOpt('description') && (
              <View style={styles.descriptionText}>
                <Text style={styles.title}>Descrição do chamado</Text>
                <Text style={styles.description}>
                  {serviceOrder?.description || serviceOrder?.get?.('description')}
                </Text>
              </View>
            )}
            {isOpt('budgetDescribed') && (
              <>
                <View style={isNeutralContractType ? styles.grayBackground : styles.backgroundText}>
                  <Text style={isNeutralContractType ? styles.blackText : styles.blueText}>
                    Orçamento detalhado
                  </Text>
                </View>
                <View style={styles.tableContainer}>
                  <View style={styles.rowContainer}>
                    <Text style={[styles.rowDescription, styles.gray80]}>Descrição</Text>
                    <Text style={[styles.rowValues, styles.gray80]}>Valores</Text>
                  </View>
                  {budget?.budgetItems
                    ?.sort((a, b) => a.id - b.id)
                    ?.map((item, index) => (
                      <View style={styles.rowContainer} key={item.id}>
                        <View style={styles.rowDescription}>
                          <Text>{item?.description}</Text>
                        </View>
                        <View style={styles.rowValues}>
                          <Text>
                            {isOpt('budgetItemsWithValues')
                              ? itemValueCalculate(item?.price, index)
                              : ''}
                          </Text>
                        </View>
                      </View>
                    ))}
                  <View style={styles.finalValuesContainer}>
                    <View style={[styles.rowContainer, styles.totalBackground]}>
                      <Text style={[styles.rowDescriptionTotal, styles.gray80]}>Total</Text>
                      <Text style={[styles.rowValuesTotal, styles.gray100]}>
                        {isOpt('budgetItemsWithValues')
                          ? formatterNumberOnly.format(handleTotalItems)
                          : ''}
                      </Text>
                    </View>
                    {isOpt('budgetItemsWithValues') && budget?.withholdingTaxValue > 0 && (
                      <>
                        <View style={styles.rowContainer}>
                          <View style={styles.rowDescription}>
                            <Text>Imposto retido: </Text>
                          </View>
                          <View style={styles.rowValues}>
                            <Text>{formatterNumberOnly.format(budget?.withholdingTaxValue)}</Text>
                          </View>
                        </View>
                        <View style={[styles.rowContainer, styles.totalBackground]}>
                          <Text style={[styles.rowDescriptionTotal, styles.gray80]}>
                            Total geral:
                          </Text>
                          <Text style={[styles.rowValuesTotal, styles.gray100]}>
                            {formatterNumberOnly.format(
                              handleTotalItems - budget?.withholdingTaxValue
                            )}
                          </Text>
                        </View>
                      </>
                    )}
                  </View>
                </View>
              </>
            )}
            {showReferaValue && (
              <View style={styles.referaValueContainer}>
                <Text>
                  *{referaPlusAgencyPercentage}% referente à suporte ao longo de todas as etapas do
                  serviço, gestão de prestadores, orçamentação, e garantia de obra {'\n'}*
                  {tradesmanPercentage}% referente a materiais e mão de obra.
                </Text>
              </View>
            )}
            <View>
              <Text style={styles.installmentLabelContainer}>Garantia do prestador (meses)</Text>
              <View style={styles.inputInstallmentsText}>
                <Text>{budget?.warrantyMonths}</Text>
              </View>
              {isNeutralContractType ? null : (
                <>
                  {coverageMessage && (
                    <View style={styles.referaValueContainer}>
                      <Text>{coverageMessage}</Text>
                    </View>
                  )}
                </>
              )}
            </View>
            <View style={styles.otherInfosContainer}>
              <View style={styles.otherInfosItemContainer}>
                <Text style={styles.hidden}>Prazo para iniciar execução</Text>
              </View>
            </View>
          </View>
        </View>
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
          fixed
        />
      </Page>
      {(isOpt('attachmentsOpen') || isOpt('attachmentsFinished')) && (
        <Page size="A4" style={styles.page}>
          <View style={isNeutralContractType ? styles.neutralBackground : styles.header}>
            <View>
              {isNeutralContractType &&
              agencyLogo &&
              serviceOrder?.agency?.get('contractType') !== 'SAAS' ? (
                <Image style={styles.headerLogo} src={agencyLogo} />
              ) : null}
            </View>
          </View>
          {agencyLogo && <Image style={styles.agencyLogo} src={agencyLogo} />}
          <View style={styles.pageSpace}>
            <View style={isNeutralContractType ? styles.grayBackground : styles.backgroundText}>
              <Text style={isNeutralContractType ? styles.blackText : styles.blueText}>Anexos</Text>
            </View>
            <View>
              {(() => {
                const imagesToShow = [
                  ...(isOpt('attachmentsOpen') ? orderPicturesOpen : []),
                  ...(isOpt('attachmentsFinished') ? orderPicturesFinished : []),
                ]
                return imagesToShow?.length > 0 ? (
                  imagesToShow.map(attachment => (
                    <View style={styles.attachmentContainer} key={attachment.id}>
                      <Image style={styles.attachmentImage} src={attachment.picture} />
                    </View>
                  ))
                ) : (
                  // Espaço reservado, porém em branco quando não existem anexos válidos
                  <View style={styles.attachmentContainer}>
                    <Text style={styles.gray80}> </Text>
                  </View>
                )
              })()}
            </View>
          </View>
          <Text
            style={styles.pageNumber}
            render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
            fixed
          />
        </Page>
      )}

      {isOpt('providerInvoice') && (
        <Page size="A4" style={styles.page}>
          <View style={isNeutralContractType ? styles.neutralBackground : styles.header}>
            <View>
              {isNeutralContractType &&
              agencyLogo &&
              serviceOrder?.agency?.get('contractType') !== 'SAAS' ? (
                <Image style={styles.headerLogo} src={agencyLogo} />
              ) : null}
            </View>
          </View>
          <Text
            style={styles.pageNumber}
            render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
            fixed
          />
        </Page>
      )}
    </Document>
  )
}

ServiceOrderPDF.propTypes = {
  budget: PropTypes.shape({
    budgetItems: PropTypes.arrayOf(PropTypes.shape({})),
    id: PropTypes.number,
    serviceOrder: PropTypes.number,
    acceptsWarranty: PropTypes.bool,
    warrantyMonths: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    totalPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    budgetPictures: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  selectedOptions: PropTypes.shape({}),
}

ServiceOrderPDF.defaultProps = {
  budget: { budgetItems: [], totalPrice: 0 },
  selectedOptions: {},
}

export default ServiceOrderPDF
